import React, {
  type FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import type { SignInLocationState } from "../App";
import { api, ApiClientError } from "../api";
import { useAuth } from "../auth";
import { useDocumentTitle } from "../document-title";

export function SignInPage() {
  const [mode, setMode] = useState<"email" | "key">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [key, setKey] = useState("");
  const [codeRequested, setCodeRequested] = useState(false);
  const [devCode, setDevCode] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dialogRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    const state = location.state as SignInLocationState | null;
    if (state?.backgroundLocation) {
      navigate(-1);
      return;
    }
    navigate("/", { replace: true });
  }, [location.state, navigate]);

  useEffect(() => {
    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const appShell = document.querySelector<HTMLElement>(".app-shell");
    document.body.classList.add("auth-modal-open");
    appShell?.setAttribute("inert", "");
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );
      const first = focusable.at(0);
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.classList.remove("auth-modal-open");
      appShell?.removeAttribute("inert");
      document.removeEventListener("keydown", onKeyDown);
      window.setTimeout(() => {
        const mobileMenuButton = document.querySelector<HTMLElement>(
          '[aria-label="Toggle navigation"]',
        );
        const signInLink = document.querySelector<HTMLElement>(
          '.profile-link[href="/sign-in"]',
        );
        const mobileMenuIsVisible =
          mobileMenuButton &&
          window.getComputedStyle(mobileMenuButton).display !== "none";
        (mobileMenuIsVisible
          ? mobileMenuButton
          : previouslyFocused?.isConnected
            ? previouslyFocused
            : signInLink
        )?.focus();
      });
    };
  }, [close]);

  const finish = async () => {
    await refresh();
    const state = location.state as SignInLocationState | null;
    navigate(state?.from ?? "/library?scope=mine", { replace: true });
  };

  const showEmail = () => {
    setMode("email");
    setCodeRequested(false);
    setCode("");
    setError("");
  };

  const showRecoveryKey = () => {
    setMode("key");
    setError("");
  };

  const requestCode = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await api<{ developmentCode?: string }>(
        "/api/auth/request-code",
        {
          method: "POST",
          body: JSON.stringify({ email }),
        },
      );
      setDevCode(response.developmentCode ?? null);
      setCodeRequested(true);
    } catch (caught) {
      setError(
        caught instanceof ApiClientError
          ? caught.message
          : "Unable to request a code.",
      );
    } finally {
      setBusy(false);
    }
  };

  const verify = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api("/api/auth/verify-code", {
        method: "POST",
        body: JSON.stringify({ email, code }),
      });
      await finish();
    } catch (caught) {
      if (
        caught instanceof ApiClientError &&
        caught.code === "RECOVERY_KEY_REQUIRED"
      ) {
        setMode("key");
        setCodeRequested(false);
        setCode("");
      }
      setError(
        caught instanceof ApiClientError
          ? caught.message
          : "Unable to sign in.",
      );
      setBusy(false);
    }
  };

  const signInWithKey = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api("/api/auth/password", {
        method: "POST",
        body: JSON.stringify({ email, key }),
      });
      await finish();
    } catch (caught) {
      setError(
        caught instanceof ApiClientError
          ? caught.message
          : "Unable to sign in.",
      );
      setBusy(false);
    }
  };

  const title =
    mode === "key"
      ? "Use your emergency recovery key"
      : codeRequested
        ? "Check your email"
        : "Sign in to StanceKit";
  useDocumentTitle(title);

  return (
    <section className="auth-modal-layer" aria-label="Account access">
      <div className="auth-modal-backdrop" aria-hidden="true" onClick={close} />
      <div
        ref={dialogRef}
        className="auth-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sign-in-title"
      >
        <h1 id="sign-in-title">{title}</h1>
        {mode === "email" && !codeRequested ? (
          <p className="auth-intro">
            Enter your email to receive a six-digit sign-in code.
          </p>
        ) : null}
        {mode === "key" ? (
          <p className="auth-intro">
            Enter the key shown in your account settings.
          </p>
        ) : null}
        {error ? (
          <div className="alert alert-error" role="alert">
            {error}
          </div>
        ) : null}
        {mode === "email" && !codeRequested ? (
          <form onSubmit={(event) => void requestCode(event)}>
            <label>
              Email address
              <input
                type="email"
                required
                autoFocus
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
              />
            </label>
            <button className="button button-full" disabled={busy}>
              {busy ? "Sending code…" : "Sign in"}
            </button>
          </form>
        ) : null}
        {mode === "email" && codeRequested ? (
          <form onSubmit={(event) => void verify(event)}>
            <p className="code-destination" aria-live="polite">
              We sent a sign-in code to <strong>{email}</strong>.
            </p>
            <label>
              Six-digit code
              <input
                className="code-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                autoFocus
                value={code}
                onChange={(event) =>
                  setCode(event.target.value.replace(/\D/g, ""))
                }
                placeholder="000000"
              />
            </label>
            {devCode ? (
              <div className="alert">
                Development code: <strong>{devCode}</strong>
              </div>
            ) : null}
            <button className="button button-full" disabled={busy}>
              {busy ? "Verifying…" : "Sign in"}
            </button>
          </form>
        ) : null}
        {mode === "key" ? (
          <form onSubmit={(event) => void signInWithKey(event)}>
            <label>
              Email address
              <input
                type="email"
                required
                autoFocus
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <label>
              Emergency recovery key
              <input
                className="code-input code-input-wide"
                required
                autoCapitalize="characters"
                autoComplete="current-password"
                value={key}
                onChange={(event) => setKey(event.target.value.toUpperCase())}
                placeholder="XXXX-XXXX-XXXX"
              />
            </label>
            <button className="button button-full" disabled={busy}>
              {busy ? "Checking key…" : "Sign in with recovery key"}
            </button>
          </form>
        ) : null}
        <div className="auth-modal-options">
          <button className="text-button" type="button" onClick={close}>
            Continue without signing in
          </button>
          {mode === "email" && codeRequested ? (
            <button className="text-button" type="button" onClick={showEmail}>
              Use a different email
            </button>
          ) : mode === "email" ? (
            <button
              className="text-button"
              type="button"
              onClick={showRecoveryKey}
            >
              Use emergency recovery key
            </button>
          ) : (
            <button className="text-button" type="button" onClick={showEmail}>
              Use email code
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
